<template>
    <div class="tw-w-full tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-primary-200 tw-overflow-hidden tw-flex tw-flex-col">
        <!-- Chat Header -->
        <div class="tw-px-4 tw-py-3 tw-border-bx tw-border-solid tw-border-gray-200 tw-bg-gray-50" :class="{ 'tw-border-b': isChatExpanded }">
            <div class="tw-flex tw-items-center tw-gap-3">
                <div class="tw-w-8 tw-h-8 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                    <icon icon="solar:chat-round-dots-bold-duotone" class="tw-text-primary-600 tw-text-sm" />
                </div>
                <div class="tw-flex-1">
                    <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">{{ companyName || 'Company' }}</h3>
                    <p class="tw-text-xs tw-text-gray-500">Online now</p>
                </div>
                <div class="tw-flex tw-items-center tw-gap-1">
                    <button 
                        class="tw-p-1 tw-rounded-md hover:tw-bg-primary-500 tw-text-primary-500 hover:tw-text-white tw-bg-white tw-transition-colors tw-border tw-border-solid tw-border-gray-200 hover:tw-border-primary-200"
                        title="Start a video call"
                    >
                            <icon 
                                icon="solar:videocamera-record-bold-duotone" 
                            class=" tw-text-xl" 
                        />
                    </button>

                    <button 
                        @click="toggleChat"
                        class="tw-p-1 tw-rounded-md hover:tw-bg-gray-100x tw-bg-white tw-transition-colors tw-border tw-border-solid tw-border-gray-200 hover:tw-border-primary-200"
                        :title="isChatExpanded ? 'Collapse chat' : 'Expand chat'"
                    >
                        <icon 
                            :icon="isChatExpanded ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'" 
                            class="tw-text-gray-500 tw-text-xl" 
                        />
                    </button>
                </div>
            </div>
        </div>

        <!-- Chat Content - Hidden when collapsed -->
        <div v-if="isChatExpanded" class="tw-flex tw-flex-col tw-flex-1">
            <!-- Messages Area -->
            <div ref="messagesContainer" class="tw-flex-1 tw-p-4 tw-overflow-y-auto tw-space-y-3 tw-max-h-[400px]">
                <!-- Dynamic Messages -->
                <div 
                    v-for="message in messages" 
                    :key="message.id"
                    :class="[
                        'tw-flex tw-items-start tw-gap-2',
                        message.type === 'user' ? 'tw-justify-end' : ''
                    ]"
                >
                    <!-- Company Avatar -->
                    <div 
                        v-if="message.type === 'company'"
                        class="tw-w-6 tw-h-6 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0"
                    >
                        <icon icon="solar:buildings-2-bold-duotone" class="tw-text-primary-600 tw-text-xs" />
                    </div>
                    
                    <!-- Message Content -->
                    <div :class="[
                        'tw-flex-1',
                        message.type === 'user' ? 'tw-text-right' : ''
                    ]">
                        <div :class="[
                            'tw-rounded-lg tw-px-3 tw-py-2 tw-max-w-[85%]',
                            message.type === 'user' 
                                ? 'tw-bg-primary-500 tw-text-white tw-rounded-tr-none tw-ml-auto' 
                                : 'tw-bg-gray-100 tw-text-gray-800 tw-rounded-tl-none'
                        ]">
                            <p class="tw-text-sm">{{ message.text }}</p>
                        </div>
                        <p class="tw-text-xs tw-text-gray-500 tw-mt-1">{{ message.timestamp }}</p>
                    </div>
                    
                    <!-- User Avatar -->
                    <div 
                        v-if="message.type === 'user'"
                        class="tw-w-6 tw-h-6 tw-bg-primary-500 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0"
                    >
                        <icon icon="solar:user-bold-duotone" class="tw-text-white tw-text-xs" />
                    </div>
                </div>

                <!-- Typing Indicator (show when company is typing) -->
                <div v-if="isTyping" class="tw-flex tw-items-start tw-gap-2">
                    <div class="tw-w-6 tw-h-6 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                        <icon icon="solar:buildings-2-bold-duotone" class="tw-text-primary-600 tw-text-xs" />
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-2">
                        <div class="tw-w-2 tw-h-2 tw-bg-gray-400 tw-rounded-full tw-animate-bounce"></div>
                        <div class="tw-w-2 tw-h-2 tw-bg-gray-400 tw-rounded-full tw-animate-bounce" style="animation-delay: 0.1s;"></div>
                        <div class="tw-w-2 tw-h-2 tw-bg-gray-400 tw-rounded-full tw-animate-bounce" style="animation-delay: 0.2s;"></div>
                    </div>
                </div>
            </div>

            <!-- Message Input -->
            <div class="tw-p-4 tw-border-t tw-border-solid tw-border-gray-200 tw-bg-gray-50">
                <!-- Enhanced Input Area -->
                <div class="tw-relative tw-bg-white tw-rounded-lg tw-border tw-border-gray-300 tw-shadow-sm tw-focus-within:tw-ring-2 tw-focus-within:tw-ring-primary-500 tw-focus-within:tw-border-primary-500 tw-transition-all">
                    <!-- Textarea -->
                    <textarea 
                        v-model="messageText"
                        @keydown.enter.prevent="sendMessage"
                        @input="handleInput"
                        placeholder="Type your message..."
                        class="tw-w-full tw-px-4 tw-py-3 tw-text-sm tw-border-0 tw-rounded-lg tw-resize-none tw-outline-none tw-bg-transparent tw-min-h-[10px] tw-max-h-[120px]"
                        rows="1"
                        ref="messageInput"
                    ></textarea>
                    
                    <!-- Input Actions -->
                    <div class="tw-flex tw-items-center tw-justify-end tw-px-3 tw-pb-2">
                        <!-- Right Actions -->
                        <div class="tw-flex tw-items-center tw-gap-1">
                            <button 
                                v-if="messageText.trim()"
                                @click="clearMessage"
                                class="tw-p-2 tw-text-gray-400 hover:tw-text-red-500 hover:tw-bg-red-50 tw-rounded-md tw-transition-all tw-duration-200 tw-group"
                                title="Clear message"
                            >
                                <icon icon="solar:close-circle-bold-duotone" class="tw-text-sm group-hover:tw-scale-110 tw-transition-transform" />
                            </button>
                            <button 
                                @click="sendMessage"
                                :disabled="!messageText.trim()"
                                class="tw-p-2 tw-bg-primary-500 tw-text-white tw-rounded-md hover:tw-bg-primary-600 disabled:tw-bg-gray-300 disabled:tw-cursor-not-allowed tw-transition-all tw-duration-200 tw-flex-shrink-0 tw-group tw-shadow-sm hover:tw-shadow-md"
                                title="Send message"
                            >
                                <icon 
                                    :icon="messageText.trim() ? 'solar:map-arrow-right-bold-duotone' : 'solar:map-arrow-right-linear'" 
                                    class="tw-text-sm group-hover:tw-scale-110 tw-transition-transform" 
                                />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Quick Reply Suggestions -->
                <div v-if="!messageText.trim()" class="tw-flex tw-items-center tw-gap-2 tw-mt-3 tw-flex-wrap">
                    <button 
                        v-for="suggestion in quickReplies" 
                        :key="suggestion"
                        @click="useQuickReply(suggestion)"
                        class="tw-px-3 tw-py-1 tw-text-xs tw-bg-white tw-border tw-border-gray-200 tw-rounded-full hover:tw-bg-primary-50 hover:tw-border-primary-200 tw-transition-all tw-duration-200 tw-text-gray-600 hover:tw-text-primary-700"
                    >
                        {{ suggestion }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';

// Props
defineProps({
    companyName: {
        type: String,
        default: 'Company'
    }
});

// Emits
const emit = defineEmits(['message-sent']);

// Reactive data
const messageText = ref('');
const messages = ref([
    {
        id: 1,
        type: 'company',
        text: 'Hello! Welcome to our company profile. How can we help you today?',
        timestamp: '2:30 PM'
    },
    {
        id: 2,
        type: 'user',
        text: 'Hi! I\'m interested in learning more about your products and services.',
        timestamp: '2:32 PM'
    },
    {
        id: 3,
        type: 'company',
        text: 'Great! We offer a wide range of products and services. Would you like to schedule a meeting to discuss your specific needs?',
        timestamp: '2:33 PM'
    }
]);
const isTyping = ref(false);
const messagesContainer = ref(null);
const messageInput = ref(null);
const isChatExpanded = ref(true); // Start expanded by default

// Quick reply suggestions
const quickReplies = ref([
    'Hello!',
    'I\'m interested in your products',
    'Can we schedule a meeting?',
    'Tell me more about your services',
    'What are your prices?'
]);

// Auto-resize textarea
const handleInput = () => {
    if (messageInput.value) {
        messageInput.value.style.height = 'auto';
        messageInput.value.style.height = Math.min(messageInput.value.scrollHeight, 120) + 'px';
    }
};

// Clear message
const clearMessage = () => {
    messageText.value = '';
    if (messageInput.value) {
        messageInput.value.style.height = 'auto';
    }
};

// Use quick reply
const useQuickReply = (reply) => {
    messageText.value = reply;
    handleInput();
    messageInput.value?.focus();
};

// Scroll to bottom of messages
const scrollToBottom = () => {
    setTimeout(() => {
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    }, 100);
};

// Send message function
const sendMessage = () => {
    if (!messageText.value.trim()) return;
    
    // Add user message
    const userMessage = {
        id: Date.now(),
        type: 'user',
        text: messageText.value.trim(),
        timestamp: new Date().toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
        })
    };
    
    messages.value.push(userMessage);
    messageText.value = '';
    scrollToBottom();
    
    // Emit message sent event
    emit('message-sent', userMessage);
    
    // Show typing indicator
    isTyping.value = true;
    
    // Simulate company response after 2 seconds
    setTimeout(() => {
        isTyping.value = false;
        const companyResponse = {
            id: Date.now() + 1,
            type: 'company',
            text: 'Thank you for your message! Our team will get back to you shortly.',
            timestamp: new Date().toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true 
            })
        };
        messages.value.push(companyResponse);
        scrollToBottom();
    }, 2000);
};

// Toggle chat expansion
const toggleChat = () => {
    isChatExpanded.value = !isChatExpanded.value;
};
</script>

<style scoped></style> 